import { formatLocalDate } from '../utils/localDateTime';
import { confirmApp, showAppAlert } from '../common/appDialog';
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { Organization, UserProfile, Page, SrmProject, ActiveFramework, AcapsCrisis, DailyEnterpriseAudit, RiskEvent, GeopoliticalEvent, RiskEntry } from '@types';
import { collection, query, where, getDocs, orderBy, limit, doc, updateDoc, deleteDoc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebaseConfig';
import { GlobeAltIcon, ShieldCheckIcon, ShieldExclamationIcon, BeakerIcon, ClockIcon, ArrowRightIcon, BuildingOfficeIcon, BoltIcon, CheckCircleIcon, ExclamationTriangleIcon, Cog6ToothIcon, XMarkIcon, ArrowPathIcon, PresentationChartLineIcon, MapIcon, InformationCircleIcon, TrashIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { RiskMonitorDashboard } from './RiskMonitorDashboard';
import useGoogleMaps from './useGoogleMaps';
import { ExecutiveReportBuilder, ReportConfig } from './ExecutiveReportBuilder';
import { EnterpriseFsatEngine } from './EnterpriseFsatEngine';
import { EnterpriseAuditEngine } from './EnterpriseAuditEngine';
import { useGlobalAssets } from '../context/GlobalAssetContext';
import { BusinessMethodologySidebar } from './BusinessMethodologySidebar';
import { ICONS, industryConfig } from './constants';
import { GlobalLiveNewsGrid } from './GlobalLiveNewsGrid';
import { organizationCoverageCountries } from '../utils/liveNewsRegions';
import { getOrganizationDisplayName } from '@common/organizationDisplayName';
import ClientServiceOverview from './ClientServiceOverview';
import CountryDashboardCards from './CountryDashboardCards';
import AnalyzeIntelligenceModal from './AnalyzeIntelligenceModal';
import { intelligenceAnalysisStyles } from '../common/intelligenceAnalysisStyles';
import { canUseServiceRequests } from '../common/clientAccessPolicy';
import { canManageClientAccount } from '../common/clientAccountAccess';
import { isCompletedServiceDelivery, serviceDeliveryWorkspace, type CompletedServiceRequest } from '../utils/completedServiceDeliveries';
import { dashboardCountry } from '../utils/clientDashboardCountries';

const safeToDate = (val: any): Date => {
    if (!val) return new Date();
    if (typeof val.toDate === 'function') return val.toDate();
    if (val instanceof Date) return val;
    if (typeof val === 'string' || typeof val === 'number') return new Date(val);
    if (val.seconds !== undefined) return new Date(val.seconds * 1000);
    return new Date();
};

type CompletedItem = {
    id: string;
    title: string;
    detail: string;
    completedAt: Date;
    target?: { page: string; params?: any };
};

interface Props {
    organization: Organization;
    currentUser: UserProfile;
    onNavigate: (nav: { page: string; params?: any }) => void;
    riskData?: any[];
    alerts?: (RiskEvent | GeopoliticalEvent)[];
    onRequestHelp?: (context: any) => void;
    readOnly?: boolean;
}

const OrganizationDashboard: React.FC<Props> = ({ organization, currentUser, onNavigate, riskData, alerts, onRequestHelp, readOnly = false }) => {
    const [projectCount, setProjectCount] = useState<number>(0);
    const [frameworkCount, setFrameworkCount] = useState<number>(0);
    const [recentProjects, setRecentProjects] = useState<SrmProject[]>([]);
    const [recentFrameworks, setRecentFrameworks] = useState<ActiveFramework[]>([]);
    const [completedServices, setCompletedServices] = useState<CompletedServiceRequest[]>([]);
    const [latestAudit, setLatestAudit] = useState<DailyEnterpriseAudit | null>(null);
    const [globalRisks, setGlobalRisks] = useState<RiskEntry[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [auditFreq, setAuditFreq] = useState(organization?.auditPreferences?.frequency || 'manual');
    const [industry, setIndustry] = useState(organization?.industry || '');
    const [region, setRegion] = useState(organization?.region || '');
    const [isSaving, setIsSaving] = useState(false);
    const { isLoaded } = useGoogleMaps();
    const { assets } = useGlobalAssets();
    const [isReportBuilderOpen, setIsReportBuilderOpen] = useState(false);
    const [isIntelligenceAnalysisOpen, setIsIntelligenceAnalysisOpen] = useState(false);
    const [isFsatEngineOpen, setIsFsatEngineOpen] = useState(false);
    const [isAuditEngineOpen, setIsAuditEngineOpen] = useState(false);
    const [isGeneratingForecast, setIsGeneratingForecast] = useState(false);
    const [forecastFeedback, setForecastFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const forecastSectionRef = useRef<HTMLDivElement | null>(null);
    const [isGeneratingExecReport, setIsGeneratingExecReport] = useState(false);
    const [isMethodologyOpen, setIsMethodologyOpen] = useState(false);
    const [focusedCrisis, setFocusedCrisis] = useState<{lat: number, lng: number, zoom?: number} | null>(null);
    const [expandedCrisisId, setExpandedCrisisId] = useState<string | null>(null);
    const [pendingUsersCount, setPendingUsersCount] = useState<number>(0);
    const [showResolutionConfirm, setShowResolutionConfirm] = useState(false);
    const [resolutionMsg, setResolutionMsg] = useState('');
    const [resultProjectId, setResultProjectId] = useState<string | null>(null);
    const [remediationStates, setRemediationStates] = useState<Record<string, {
        status: 'pending' | 'executing' | 'completed' | 'failed',
        progress: number,
        statusText: string,
        resultId?: string
    }>>({});
    const [isExecutingAll, setIsExecutingAll] = useState(false);

    useEffect(() => {
        if (!isSettingsOpen)
            return;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape')
                setIsSettingsOpen(false);
        };
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isSettingsOpen]);
    
    const pinnedSuites: string[] = ((organization as any)?.pinnedSuites || []).filter((suite: string) => !suite.toLowerCase().includes('hsse'));

    const handleSaveSettings = async () => {
        setIsSaving(true);
        try {
            await updateDoc(doc(db, 'organizations', organization.id), {
                'auditPreferences.frequency': auditFreq,
                industry: industry,
                region: region
            });
            setIsSettingsOpen(false);
        } catch(err) {
            console.error("Save err:", err);
        }
        setIsSaving(false);
    };

    useEffect(() => {
        if (!db || !organization?.id) return;
        
        setIsLoading(true);

        // 1. Listen for active SRM projects
        const srmQuery = query(
            collection(db, 'srm_projects'),
            where('organizationId', '==', organization.id),
            orderBy('createdAt', 'desc'),
            limit(20)
        );
        const unsubscribeSrm = onSnapshot(srmQuery, (srmSnapshot) => {
            const allSrmProjects = srmSnapshot.docs
                .map(doc => ({ ...doc.data(), id: doc.id } as SrmProject))
                .filter(p => 
                    (p.status === 'Draft' || p.status === 'Review' || p.status === 'Active' || p.status === 'Architecting') && 
                    !p.id.startsWith('PROJ-ARCHITECT-') &&
                    !p.modules?.some(m => m.workflowId === 'country_geopolitical_analysis')
                );
            setProjectCount(allSrmProjects.length);
            setRecentProjects(allSrmProjects.slice(0, 5));
        }, (err) => console.error("SRM listener err:", err));

        // 2. Listen for active frameworks
        const fwQuery = query(
            collection(db, 'active_frameworks'),
            where('organizationId', '==', organization.id),
            limit(5)
        );
        const unsubscribeFw = onSnapshot(fwQuery, (fwSnapshot) => {
            const fwList = fwSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ActiveFramework));
            setRecentFrameworks(fwList);
            setFrameworkCount(fwList.length);
        }, (err) => console.error("FW listener err:", err));

        // 3. Listen for latest Daily Enterprise Audit (CRITICAL for real-time resolution)
        const auditQuery = query(
            collection(db, 'daily_enterprise_audits'),
            where('organizationId', '==', organization.id),
            orderBy('timestamp', 'desc'),
            limit(1)
        );
        const unsubscribeAudit = onSnapshot(auditQuery, (auditSnapshot) => {
            if (!auditSnapshot.empty) {
                const audit = { id: auditSnapshot.docs[0].id, ...auditSnapshot.docs[0].data() } as DailyEnterpriseAudit;
                setLatestAudit(audit);
            }
            setIsLoading(false);
        }, (err) => {
            console.error("Audit listener err:", err);
            setIsLoading(false);
        });

        // 5. Listen for Global Risks
        const globalRisksQuery = query(
            collection(db, 'organizations', organization.id, 'global_risk_register'),
            orderBy('createdAt', 'desc')
        );
        const unsubscribeGlobalRisks = onSnapshot(globalRisksQuery, (snap) => {
            setGlobalRisks(snap.docs.map(doc => ({ ...doc.data(), id: doc.id } as RiskEntry)));
        }, (err) => console.error("Global risks listener err:", err));

        // 6. Fetch pending users (Static ok for now) - ONLY for admins
        const fetchPending = async () => {
            const hasClearance = currentUser?.role === 'owner' || currentUser?.role === 'admin';
            if (!hasClearance) return;

            try {
                const pendingUsersQuery = query(
                    collection(db, 'users'),
                    where('organizationId', '==', organization.id),
                    where('status', '==', 'pending_approval')
                );
                const pendingSnapshot = await getDocs(pendingUsersQuery);
                setPendingUsersCount(pendingSnapshot.size);
            } catch (err) {
                console.warn("Pending users fetch restricted or failed:", err);
            }
        };
        fetchPending();

        return () => {
            unsubscribeSrm();
            unsubscribeFw();
            unsubscribeAudit();
            unsubscribeGlobalRisks();
        };
    }, [db, organization?.id]);

    useEffect(() => {
        if (!organization?.id) {
            setCompletedServices([]);
            return;
        }
        let active = true;
        const callable = httpsCallable(functions, 'manageServiceRequests');
        void callable({ action: 'summary', organizationId: organization.id }).then(result => {
            if (!active)
                return;
            const requests = ((result.data as any).requests || []) as CompletedServiceRequest[];
            setCompletedServices(requests.filter(isCompletedServiceDelivery));
        }).catch(error => console.error('Completed service deliveries failed loading.', error));
        return () => { active = false; };
    }, [organization?.id]);

    const completedItems = React.useMemo<CompletedItem[]>(() => {
        const deliveredDashboardIds = new Set(completedServices.map(request => request.deliveryDashboard?.projectId).filter(Boolean));
        const frameworks = recentFrameworks.filter(framework => !deliveredDashboardIds.has(framework.projectId)).map(framework => ({
            id: `framework-${framework.id}`,
            title: framework.name,
            detail: 'SRM framework deployed',
            completedAt: safeToDate(framework.deployedAt),
            target: framework.projectId ? { page: 'srm_command_center', params: { projectId: framework.projectId } } : undefined,
        }));
        const services = completedServices.map(request => {
            const reportCount = Number(request.deliveryReportCount || 0);
            const workspace = serviceDeliveryWorkspace(request);
            const reports = request.deliveryReports || [];
            const countryIso3 = request.serviceCode === 'S-07' ? dashboardCountry(request.dailyMicroCountry)?.iso3 : undefined;
            const target = countryIso3
                ? { page: 'organizationDashboard', params: { iso3: countryIso3 } }
                : workspace === 'srm-dashboard' && request.deliveryDashboard?.projectId
                ? { page: 'srm_command_center', params: { projectId: request.deliveryDashboard.projectId, view: 'dashboard' } }
                : workspace && canUseServiceRequests(currentUser.role, currentUser.serviceRequestAccess)
                    ? { page: 'serviceRequests', params: { serviceRequestId: request.id } }
                : reports.length === 1
                    ? { page: 'analysisDisplay', params: { reportId: reports[0].id } }
                    : { page: 'reportLibrary' };
            return {
                id: `service-${request.id}`,
                title: request.serviceName,
                detail: request.serviceCode === 'S-07' && request.dailyMicroCountry
                    ? `${request.requestReference} · ${request.dailyMicroCountry} daily micro report`
                    : workspace === 'ep-project'
                    ? `${request.requestReference} · EP project workspace`
                    : workspace === 'travel-dashboard'
                        ? `${request.requestReference} · Live travel dashboard`
                        : workspace === 'srm-dashboard'
                            ? `${request.requestReference} · Deployed ${request.serviceCode || 'SRM'} dashboard and linked report`
                        : `${request.requestReference} · ${reportCount} online report${reportCount === 1 ? '' : 's'}`,
                completedAt: safeToDate(request.deliveredAt),
                target,
            };
        });
        return [...frameworks, ...services]
            .sort((left, right) => right.completedAt.getTime() - left.completedAt.getTime())
            .slice(0, 8);
    }, [completedServices, currentUser.role, currentUser.serviceRequestAccess, recentFrameworks]);

    const mappedAlerts = React.useMemo(() => {
        return (alerts || []).map(a => {
            if ('riskScore' in a) return a as RiskEvent;
            
            // It's a GeopoliticalEvent or AcapsCrisis
            const geo = a as any;
            const lat = geo.latitude ?? geo.lat ?? 0;
            const lng = geo.longitude ?? geo.lng ?? 0;
            const severity = geo.severity ?? 'medium';
            const riskScore = geo.riskScore ?? (severity === 'critical' ? 90 : severity === 'high' ? 75 : severity === 'medium' ? 50 : 25);
            
            return {
                ...a,
                summary: geo.description || geo.summary || 'Strategic Intelligence Update',
                riskScore: riskScore,
                category: geo.type || 'Geopolitical',
                location: {
                    country: geo.location?.country || 'Global',
                    city: geo.location?.city || 'Analyzing signals...',
                    lat: lat,
                    lng: lng
                },
                severity: severity,
                latitude: lat,
                longitude: lng
            } as RiskEvent;
        });
    }, [alerts]);

    // Derived threat count for Enterprise Audit — counts high/critical severity live alerts
    const activeHighCriticalThreats = React.useMemo(() => {
        return mappedAlerts.filter(a => {
            const sev = (a as any).severity?.toLowerCase();
            return sev === 'critical' || sev === 'high';
        }).length;
    }, [mappedAlerts]);

    // --- AUDIT REMEDIATION ENGINE ---
    const handleExecuteRemediation = async (promptId: string) => {
        if (!latestAudit) return;
        const promptIdx = latestAudit.auditData.dynamicPrompts.findIndex((p, idx) => (p.id || `prompt-${idx}`) === promptId);
        if (promptIdx === -1) return;
        const prompt = latestAudit.auditData.dynamicPrompts[promptIdx];
        
        // Prevent double execution
        if (remediationStates[promptId]?.status === 'executing') return;

        setRemediationStates(prev => ({
            ...prev,
            [promptId]: { status: 'executing', progress: 5, statusText: 'Initializing Remediation Engine...' }
        }));

        try {
            // Stage 1: Analyze (5-25%)
            setTimeout(() => {
                setRemediationStates(prev => {
                    if (prev[promptId]?.status !== 'executing') return prev;
                    return { ...prev, [promptId]: { ...prev[promptId], progress: 25, statusText: 'Analyzing Audit Trigger & Vector Analysis...' } };
                });
            }, 2000);

            // Stage 2: Architect (25-60%)
            setTimeout(() => {
                setRemediationStates(prev => {
                    if (prev[promptId]?.status !== 'executing') return prev;
                    return { ...prev, [promptId]: { ...prev[promptId], progress: 60, statusText: 'Architecting Mitigation Strategy & Protocols...' } };
                });
            }, 6000);

            // Stage 3: Finalize (60-90%)
            setTimeout(() => {
                setRemediationStates(prev => {
                    if (prev[promptId]?.status !== 'executing') return prev;
                    return { ...prev, [promptId]: { ...prev[promptId], progress: 90, statusText: 'Finalizing Operational Remediation Report...' } };
                });
            }, 12000);

            // AI Execution
            const analysisProxy = httpsCallable(functions, 'onDemandAnalysisProxy');
            const result = await analysisProxy({
                analysisType: 'Audit Remediation Execution',
                formData: {
                    triggerEvent: prompt.triggerEvent,
                    recommendedAction: prompt.recommendedAction,
                    targetProjectId: prompt.targetProjectId,
                    targetSuite: prompt.targetSuite,
                    targetWorkflowId: prompt.targetWorkflowId,
                    context: `Organization: ${getOrganizationDisplayName(organization)}. Industry: ${organization.industry}.`
                }
            }) as { data: { result: any } };

            // Save to Firestore (generated_reports)
            const reportsCol = collection(db, 'generated_reports');
            const newReportDoc = doc(reportsCol);
            
            const reportData = {
                ...result.data.result,
                id: newReportDoc.id,
                userEmail: currentUser.email,
                organizationId: organization.id,
                timestamp: new Date().getTime(),
                isAuditRemediation: true,
                auditId: latestAudit.id
            };

            const { setDoc } = await import('firebase/firestore');
            await setDoc(newReportDoc, reportData);

            // AUTO-RESOLVE: Call Backend resolution logic
            const resolveAudit = httpsCallable(functions, 'resolveAuditRemediation');
            const resolveRes = await resolveAudit({
                auditId: latestAudit.id,
                promptId: promptId,
                organizationId: organization.id,
                targetProjectId: prompt.targetProjectId,
                targetComponentId: (prompt as any).targetComponentId || prompt.targetWorkflowId,
                remediationResult: result.data.result
            }) as { data: { success: boolean; projectId?: string } };

            // OPTIMISTIC UPDATE: Mark as completed instead of filtering out to preserve index-based ID consistency
            if (resolveRes.data.success) {
                setLatestAudit((prev: any) => {
                    if (!prev) return null;
                    return {
                        ...prev,
                        auditData: {
                            ...prev.auditData,
                            dynamicPrompts: prev.auditData.dynamicPrompts.map((p: any, idx: number) => 
                                (p.id || `prompt-${idx}`) === promptId ? { ...p, executionStatus: 'completed' } : p
                            )
                        }
                    };
                });
            }

            const finalProjectId = resolveRes.data.projectId;
            setResultProjectId(finalProjectId || null);

            setRemediationStates(prev => ({
                ...prev,
                [promptId]: { status: 'completed', progress: 100, statusText: 'Remediation Operationalized.', resultId: newReportDoc.id }
            }));

            // Show confirmation popup
            setResolutionMsg(`Audit remediation for "${prompt.triggerEvent}" has been successfully completed. A new pending risk entry has been added to the ${finalProjectId ? "Project" : "Global"} Risk Register for your final review and approval.`);
            setShowResolutionConfirm(true);

        } catch (err) {
            console.error("Remediation failed:", err);
            setRemediationStates(prev => ({
                ...prev,
                [promptId]: { status: 'failed', progress: 0, statusText: 'Execution Failed. Manual intervention required.' }
            }));
        }
    };

    const handleManualResolve = async (promptId: string) => {
        if (!latestAudit || !(latestAudit.auditData.dynamicPrompts || []).length || !await confirmApp("Are you sure you want to mark this audit action as resolved? This will move it to the official Risk Register.")) return;
        const promptIdx = (latestAudit.auditData.dynamicPrompts || []).findIndex((p, idx) => (p.id || `prompt-${idx}`) === promptId);
        if (promptIdx === -1) return;
        const prompt = latestAudit.auditData.dynamicPrompts[promptIdx];

        try {
            const resolveAudit = httpsCallable(functions, 'resolveAuditRemediation');
            const resolveRes = await resolveAudit({
                auditId: latestAudit.id,
                promptId: promptId,
                organizationId: organization.id,
                targetProjectId: prompt.targetProjectId,
                targetComponentId: (prompt as any).targetComponentId || prompt.targetWorkflowId,
                status: 'Identified', // Manual resolution goes directly to Active Risk Register
                remediationResult: {
                    findingTitle: prompt.triggerEvent || "Manual Resolution",
                    category: 'Operational', // Default
                    likelihood: 'Medium',
                    impact: 'Medium',
                    score: 50,
                    mitigationPlan: "Resolved via manual intervention."
                }
            }) as { data: { success: boolean; projectId?: string } };

            setResultProjectId(resolveRes.data.projectId || null);

            // Update UI
            setLatestAudit(prev => {
                if (!prev) return null;
                return {
                    ...prev,
                    auditData: {
                        ...prev.auditData,
                        dynamicPrompts: prev.auditData.dynamicPrompts.map((p, idx) => 
                            (p.id || `prompt-${idx}`) === promptId ? { ...p, executionStatus: 'completed' } : p
                        )
                    }
                };
            });

            setResolutionMsg(`Audit action resolved successfully and synchronized with the Master Risk Register.`);
            setShowResolutionConfirm(true);
        } catch (err) {
            console.error("Manual resolve failed:", err);
            void showAppAlert("Failed to resolve action. Please try again.");
        }
    };

    const handleExecuteAll = async () => {
        if (!latestAudit || !latestAudit.auditData.dynamicPrompts) return;
        setIsExecutingAll(true);
        
        // Execute sequentially to avoid overloading rate limits or UI clutter
        for (const [idx, prompt] of (latestAudit.auditData.dynamicPrompts || []).entries()) {
            if (prompt.executionStatus === 'completed') continue;
            
            const promptId = prompt.id || `prompt-${idx}`;
            if (prompt.priority === 'High' || prompt.priority === 'Medium') {
                if (remediationStates[promptId]?.status !== 'completed') {
                    await handleExecuteRemediation(promptId);
                }
            }
        }
        setIsExecutingAll(false);
    };

    const handleGenerateForecast = async () => {
        setIsGeneratingForecast(true);
        setForecastFeedback(null);
        try {
            const triggerAudit = httpsCallable(functions, 'triggerEnterpriseRiskAudit');
            const response = await triggerAudit({ organizationId: organization.id });
            const payload = response.data as { audit?: DailyEnterpriseAudit };

            if (payload.audit)
                setLatestAudit(payload.audit);
            else {
                const auditQuery = query(
                    collection(db, 'daily_enterprise_audits'),
                    where('organizationId', '==', organization.id),
                    orderBy('timestamp', 'desc'),
                    limit(1)
                );
                const auditSnapshot = await getDocs(auditQuery);
                if (auditSnapshot.empty)
                    throw new Error('The forecast completed without returning a saved audit.');
                setLatestAudit({ id: auditSnapshot.docs[0].id, ...auditSnapshot.docs[0].data() } as DailyEnterpriseAudit);
            }

            setForecastFeedback({ type: 'success', message: 'Strategic forecast generated successfully.' });
            window.setTimeout(() => {
                forecastSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                forecastSectionRef.current?.focus({ preventScroll: true });
            }, 100);
        } catch (err: any) {
            console.error("Failed to generate strategic forecast:", err);
            const details = typeof err?.details === 'string' ? err.details : '';
            const message = (details || err?.message || 'Strategic forecast generation failed.')
                .replace(/^FirebaseError:\s*/i, '');
            setForecastFeedback({ type: 'error', message });
        } finally {
            setIsGeneratingForecast(false);
        }
    };

    const handleDeleteProject = (e: React.MouseEvent, projectId: string) => {
        e.stopPropagation();
        // Route to SRM Command Center where the structured decommission workflow lives.
        // This ensures even draft removals go through the auditable FrameworkDecommissionModal.
        onNavigate({ page: 'srm_command_center', params: { decommissionProjectId: projectId } });
    };

    if (!organization) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-[2000px] mx-auto space-y-8 animate-fade-in">
            
            {/* CANCELLATION BANNER */}
            {organization.subscriptionStatus === 'canceled' && organization.accessUntil && (
                <div className="bg-slate-900 border border-red-500/30 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-6 shadow-2xl relative overflow-hidden group">
                    <div className="absolute inset-0 bg-red-500/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="flex items-center gap-4 relative z-10">
                        <div className="w-12 h-12 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-500">
                            <ClockIcon className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="text-white font-bold text-lg">Subscription Canceled</h3>
                            <p className="text-slate-400 text-sm">
                                Your access remains active until <span className="text-white font-semibold">{formatLocalDate(organization.accessUntil)}</span>.
                                After this, your data will be preserved for 30 days before permanent deletion.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 relative z-10">
                        <a 
                            href="https://buy.paddle.com/customer-portal" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="px-6 py-3 bg-red-600 hover:bg-red-500 text-white font-bold rounded-xl transition-all shadow-lg shadow-red-600/20 flex items-center gap-2 text-sm uppercase tracking-wider"
                        >
                            <ArrowPathIcon className="w-4 h-4" />
                            Resume Subscription
                        </a>
                        <a 
                            href="https://docs.google.com/forms/d/e/1FAIpQLScFrqqgGFLkWhIeACJ07ldTTJj38J-svbgx_6bgegzbEWcDoQ/viewform?usp=publish-editor" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold rounded-xl transition-all border border-slate-700 flex items-center gap-2 text-sm uppercase tracking-wider"
                        >
                            Give Feedback
                        </a>
                    </div>
                </div>
            )}
            {/* HER0 HEADER */}
            <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 rounded-2xl shadow-xl border border-slate-700 p-8 sm:p-12 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
                <div className="absolute top-0 right-32 w-64 h-64 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
                
                <div className="relative z-10 flex min-w-0 flex-col gap-6 lg:flex-row lg:items-stretch lg:justify-between">
                    <div className="min-w-0 flex-1 lg:max-w-2xl">
                        <div className="flex flex-wrap items-center gap-3 mb-2">
                            <span className="px-3 py-1 bg-indigo-500/20 text-indigo-300 text-xs font-bold uppercase tracking-wider rounded-md border border-indigo-500/30">
                                Client Dashboard
                            </span>
                        </div>
                        <h1 className="text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-2">
                            {getOrganizationDisplayName(organization)}
                        </h1>
                        <p className="text-lg text-slate-300 max-w-2xl">
                            View service requests, delivered intelligence, billing, learning resources, consultant support, and global assets in one place.
                        </p>
                    </div>
                    <div className="flex w-full min-w-0 flex-col gap-6 lg:w-auto lg:flex-1 lg:items-end lg:justify-between">
                        <div className="flex w-full min-w-0 flex-wrap items-center justify-start gap-3 lg:justify-end">
                            <button
                                onClick={() => setIsMethodologyOpen(true)}
                                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold uppercase tracking-widest rounded transition-colors shadow-lg flex items-center border border-slate-600"
                            >
                                <ShieldCheckIcon className="w-4 h-4 mr-2 text-indigo-400" />
                                Methodology
                            </button>
                            <button
                                onClick={() => onNavigate({ page: 'global_risk_register' })}
                                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-indigo-400 text-xs font-bold uppercase tracking-widest rounded transition-colors shadow-lg flex items-center border border-indigo-500/30"
                            >
                                <ShieldExclamationIcon className="w-4 h-4 mr-2" />
                                Master Risk Register
                            </button>
                            {!readOnly && <>
                            <button
                                onClick={() => setIsReportBuilderOpen(true)}
                                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold uppercase tracking-widest rounded transition-colors shadow-lg shadow-indigo-600/30 flex items-center"
                            >
                                <span className="mr-2">{ICONS.document}</span>
                                Generate Executive Report
                            </button>
                            <button
                                onClick={() => {
                                    if (!organization.industry || !organization.region) {
                                        void showAppAlert("Missing Organizational Metadata: Please configure Industry and Region in Company Settings before generating reports to ensure grounded analysis.");
                                        setIsSettingsOpen(true);
                                        return;
                                    }
                                    handleGenerateForecast();
                                }}
                                disabled={isGeneratingForecast}
                                className={`px-4 py-2 ${isGeneratingForecast ? 'bg-emerald-800 cursor-wait' : 'bg-emerald-600 hover:bg-emerald-500'} text-white text-xs font-bold uppercase tracking-widest rounded transition-colors shadow-lg flex items-center group/btn relative`}
                            >
                                {isGeneratingForecast ? (
                                    <><ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" /> Generating Forecast...</>
                                ) : (
                                    <><PresentationChartLineIcon className="w-4 h-4 mr-2" /> Generate Strategic Forecast</>
                                )}
                            </button>
                            <button
                                onClick={() => setIsAuditEngineOpen(true)}
                                className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white text-xs font-bold uppercase tracking-widest rounded transition-colors shadow-lg flex items-center border border-red-400/50 relative group/btn"
                            >
                                <span className="mr-2">{ICONS.activity}</span>
                                Initiate Company Audit
                            </button>
                            <button
                                onClick={() => setIsFsatEngineOpen(true)}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold uppercase tracking-widest rounded transition-colors shadow-lg flex items-center border border-blue-400/50 relative group/btn"
                            >
                                <span className="mr-2">{ICONS.shield}</span>
                                Initiate FSAT Sweep
                            </button>
                            <button
                                onClick={() => setIsSettingsOpen(true)}
                                className="bg-white/10 hover:bg-white/20 text-white rounded-md px-4 py-2 flex items-center justify-center transition-all border border-white/10 shadow-lg group"
                            >
                                <Cog6ToothIcon className="w-5 h-5 group-hover:rotate-45 transition-transform duration-300" />
                            </button>
                            </>}
                        </div>
                        <button type="button" onClick={() => setIsIntelligenceAnalysisOpen(true)}
                            className={[intelligenceAnalysisStyles.primary, 'max-w-full shrink-0 self-start sm:whitespace-nowrap lg:self-end'].join(' ')}>
                            <BeakerIcon className="h-5 w-5" aria-hidden="true" />
                            Analyze Intelligence
                        </button>
                    </div>
                </div>
            </div>

            {isIntelligenceAnalysisOpen && <AnalyzeIntelligenceModal organizationId={organization.id}
                onClose={() => setIsIntelligenceAnalysisOpen(false)}
                onOpenReport={reportId => {
                    setIsIntelligenceAnalysisOpen(false);
                    onNavigate({ page: 'analysisDisplay', params: { reportId } });
                }} />}

            <CountryDashboardCards organization={organization} deliveredCountries={completedServices.filter(request => request.serviceCode === 'S-07' && request.dailyMicroCountry).map(request => request.dailyMicroCountry!)} onOpen={iso3 => onNavigate({ page: 'organizationDashboard', params: { iso3 } })} />
            {readOnly && <ClientServiceOverview organization={organization} onOpenRequests={() => onNavigate({ page: 'serviceRequests' })} onOpenLibrary={() => onNavigate({ page: 'reportLibrary' })} onOpenBilling={() => onNavigate({ page: 'subscriptionManagement' })} canOpenRequests={canUseServiceRequests(currentUser.role, currentUser.serviceRequestAccess)} canOpenBilling={canManageClientAccount(currentUser.role)} />}

            {!readOnly && forecastFeedback && (
                <div
                    role={forecastFeedback.type === 'error' ? 'alert' : 'status'}
                    className={`rounded-xl border px-5 py-4 text-sm font-semibold ${forecastFeedback.type === 'error'
                        ? 'border-red-200 bg-red-50 text-red-800'
                        : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`}
                >
                    {forecastFeedback.message}
                </div>
            )}

            {/* GLOBAL MONITORING HUB */}
            <section className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-2xl relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-full blur-3xl group-hover:bg-red-500/10 transition-all"></div>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                    <div className="flex items-center gap-3">
                        <div className="h-8 w-1.5 bg-red-500 rounded-full animate-pulse shadow-[0_0_10px_rgba(239,68,68,0.5)]"></div>
                        <div>
                            <h2 className="text-xl font-bold text-white tracking-tight">ECC Global Command Center: 24/7 Monitoring</h2>
                            <p className="text-slate-400 text-xs font-medium uppercase tracking-[0.2em]">Real-time OSINT & Live Broadcast Hub</p>
                        </div>
                    </div>
                </div>
                <GlobalLiveNewsGrid layout="horizontal" maxFeeds={8} coverageCountries={organizationCoverageCountries(organization)} />
            </section>

            {/* MASTER RISK REGISTRY OVERSIGHT */}
            <section className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                <div className="xl:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                    <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-indigo-600 rounded-lg text-white shadow-lg shadow-indigo-100">
                                <ShieldExclamationIcon className="w-5 h-5" />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Master Risk Registry: Enterprise Oversight</h2>
                                <p className="text-xs text-slate-500 font-medium italic">Aggregated threat matrix across all organic business units</p>
                            </div>
                        </div>
                        <button 
                            onClick={() => onNavigate({ page: 'global_risk_register' })}
                            className="text-[10px] font-black text-indigo-600 uppercase tracking-widest hover:underline flex items-center gap-1.5"
                        >
                            Open Full Register
                            <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                        </button>
                        <button 
                            onClick={() => onRequestHelp?.({
                                suiteName: 'Enterprise Oversight',
                                workflowName: 'Global Master Risk Register',
                                industry: organization.industry
                            })}
                            className="p-1.5 hover:bg-white rounded-lg border border-transparent hover:border-slate-200 transition-all text-slate-400 hover:text-indigo-600"
                            title="Get AI Assistant Help"
                        >
                            <InformationCircleIcon className="w-4 h-4" />
                        </button>
                    </div>
                    
                    <div className="p-6 flex-grow">
                        {globalRisks.length === 0 ? (
                            <div className="h-full min-h-[200px] flex flex-col items-center justify-center text-slate-400 gap-3 border-2 border-dashed border-slate-100 rounded-xl">
                                <ShieldCheckIcon className="w-12 h-12 opacity-20" />
                                <p className="text-sm font-medium uppercase tracking-widest opacity-60">No Organization-Wide Risks Detected</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {globalRisks.slice(0, 5).map((risk) => (
                                    <div key={risk.id} className="flex items-center justify-between p-4 bg-slate-50 border border-slate-100 rounded-xl hover:border-indigo-200 transition-all group">
                                        <div className="flex items-center gap-4 min-w-0">
                                            <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                                                risk.score >= 15 ? 'bg-red-500 animate-pulse' : 
                                                risk.score >= 10 ? 'bg-amber-500' : 'bg-emerald-500'
                                            }`}></div>
                                            <div className="truncate">
                                                <h4 className="text-sm font-bold text-slate-900 group-hover:text-indigo-600 transition-colors truncate">{risk.threat}</h4>
                                                <div className="flex items-center gap-2 mt-0.5">
                                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{risk.category}</span>
                                                    <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                                                    <span className={`text-[10px] font-bold ${
                                                        risk.status === 'Pending' ? 'text-indigo-600 italic' : 'text-slate-500'
                                                    }`}>
                                                        {risk.status === 'Pending' ? '⚡ AI Remediation Pending Review' : risk.status}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4 flex-shrink-0">
                                            <div className="text-right">
                                                <p className={`text-lg font-black leading-none ${
                                                    risk.score >= 15 ? 'text-red-600' : 
                                                    risk.score >= 10 ? 'text-amber-600' : 'text-emerald-600'
                                                }`}>{risk.score}</p>
                                                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Rank</p>
                                            </div>
                                            <button 
                                                onClick={() => onNavigate({ page: 'global_risk_register' })}
                                                className="p-2 hover:bg-white rounded-lg border border-transparent hover:border-slate-200 transition-all text-slate-400 hover:text-indigo-600"
                                            >
                                                <ArrowRightIcon className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                {globalRisks.length > 5 && (
                                    <p className="text-[10px] text-center text-slate-400 font-bold uppercase tracking-widest pt-2">
                                        + {globalRisks.length - 5} additional global risks in master registry
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-slate-900 rounded-2xl p-8 border border-slate-700 shadow-xl relative overflow-hidden flex flex-col justify-between group">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-3xl group-hover:bg-indigo-500/20 transition-all"></div>
                    <div>
                        <div className="w-12 h-12 bg-indigo-500/20 border border-indigo-500/30 rounded-xl flex items-center justify-center text-indigo-400 mb-6">
                            <PresentationChartLineIcon className="w-6 h-6" />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2 uppercase tracking-tight italic">Risk Factorization</h3>
                        <p className="text-slate-400 text-sm leading-relaxed mb-8">
                            High-fidelity intelligence sweeps have identified <span className="text-indigo-400 font-bold">{globalRisks.filter(r => r.score >= 15).length} critical threat vectors</span> that require immediate executive synchronization.
                        </p>
                        
                        <div className="grid grid-cols-2 gap-4 mb-8">
                            <div className="bg-white/5 rounded-xl p-4 border border-white/5">
                                <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Total Risks</p>
                                <p className="text-2xl font-black text-white">{globalRisks.length}</p>
                            </div>
                            <div className="bg-white/5 rounded-xl p-4 border border-white/5">
                                <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-1">AI Identified</p>
                                <p className="text-2xl font-black text-white">{globalRisks.filter(r => r.source === 'ai_audit').length}</p>
                            </div>
                        </div>
                    </div>

                    <button 
                        onClick={() => onNavigate({ page: 'global_risk_register' })}
                        className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-black uppercase tracking-[0.2em] rounded-xl transition-all shadow-lg shadow-indigo-900/20 flex items-center justify-center gap-2"
                    >
                        <ShieldExclamationIcon className="w-4 h-4" />
                        Open Risk Command
                    </button>
                </div>
            </section>

            {/* SETTINGS MODAL */}
            {!readOnly && isSettingsOpen && createPortal(
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Company Settings"
                    className="fixed inset-0 z-[2147483647] flex h-[100dvh] w-screen items-center justify-center overflow-y-auto bg-slate-900/60 p-4 backdrop-blur-sm cursor-pointer"
                    onClick={() => setIsSettingsOpen(false)}
                >
                    <div
                        className="relative my-auto max-h-[calc(100dvh-2rem)] w-full max-w-md overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-2xl animate-fade-in cursor-default"
                        onClick={event => event.stopPropagation()}
                    >
                        <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                                <Cog6ToothIcon className="w-5 h-5 text-slate-500" /> Company Settings
                            </h3>
                            <button onClick={() => setIsSettingsOpen(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                                <span className="sr-only">Close Company Settings</span>
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>
                        <div className="p-6">
                            <h4 className="font-semibold text-slate-800 mb-2">Insights Intelligence Sweep</h4>
                            <p className="text-sm text-slate-500 mb-4">
                                Determines how frequently the Insights engine sweeps your global footprint against live GDELT threat data to produce Enterprise Control Center (ECC) action prompts.
                            </p>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Polling Cadence</label>
                            <select 
                                value={auditFreq}
                                onChange={(e) => setAuditFreq(e.target.value)}
                                className="w-full bg-slate-50 border border-slate-300 rounded-xl px-4 py-3 text-slate-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-6"
                            >
                                <option value="manual">Manual (On-Demand Only)</option>
                                <option value="hourly">Hourly (Real-Time Intelligence)</option>
                                <option value="daily_0800">Daily (08:00 Local)</option>
                                <option value="weekly_monday">Weekly (Monday 08:00)</option>
                            </select>

                            <h4 className="font-semibold text-slate-800 mb-2">Organizational Context</h4>
                            <p className="text-sm text-slate-500 mb-4">
                                Strategic metadata used to ground AI intelligence sweeps and minimize hallucinations.
                            </p>

                            <label className="block text-sm font-medium text-slate-700 mb-1">Primary Industry</label>
                            <select 
                                value={industry}
                                onChange={(e) => setIndustry(e.target.value)}
                                className="w-full bg-slate-50 border border-slate-300 rounded-xl px-4 py-3 text-slate-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-4"
                            >
                                <option value="">Select Industry...</option>
                                {Object.keys(industryConfig).map(name => (
                                    <option key={name} value={name}>{name}</option>
                                ))}
                                <option value="Other">Other / Diversified</option>
                            </select>

                            <label className="block text-sm font-medium text-slate-700 mb-1">Primary Region</label>
                            <input 
                                type="text"
                                value={region}
                                onChange={(e) => setRegion(e.target.value)}
                                placeholder="e.g. Western Europe, Southeast Asia"
                                className="w-full bg-slate-50 border border-slate-300 rounded-xl px-4 py-3 text-slate-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-6"
                            />
                            
                            <div className="flex justify-end gap-3">
                                <button 
                                    onClick={() => setIsSettingsOpen(false)}
                                    className="px-5 py-2.5 rounded-xl font-bold text-slate-600 hover:bg-slate-100 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button 
                                    onClick={handleSaveSettings}
                                    disabled={isSaving}
                                    className="px-5 py-2.5 rounded-xl font-bold bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg transition-all flex items-center gap-2"
                                >
                                    {isSaving ? "Saving..." : "Save Configuration"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* QUICK STATS METRICS RIBBON */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
                <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200 flex items-center space-x-4">
                    <div className="p-3 bg-blue-50 rounded-lg text-blue-600">
                        <BuildingOfficeIcon className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Active Org Seats</p>
                        <p className="text-2xl font-black text-slate-900">{organization.seatsUsed} / {organization.maxSeats}</p>
                    </div>
                </div>

                <div 
                    className="bg-white rounded-xl p-5 shadow-sm border border-slate-200 flex items-center space-x-4 cursor-pointer hover:border-blue-400 transition-all group"
                    onClick={() => onNavigate({ page: 'srm_command_center' })}
                >
                    <div className="p-3 bg-indigo-50 rounded-lg text-indigo-600 group-hover:bg-indigo-100">
                        <ClockIcon className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">In-Progress</p>
                        <p className="text-2xl font-black text-slate-900">{isLoading ? '-' : projectCount}</p>
                        <p className="text-[10px] text-indigo-500 font-bold group-hover:underline">Manage in Command Center →</p>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200 flex items-center space-x-4">
                    <div className="p-3 bg-emerald-50 rounded-lg text-emerald-600">
                        <CheckCircleIcon className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Completed Workflows</p>
                        <p className="text-2xl font-black text-slate-900">{isLoading ? '-' : frameworkCount}</p>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200 flex items-center space-x-4">
                    <div className="p-3 bg-amber-50 rounded-lg text-amber-600">
                        <ExclamationTriangleIcon className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Active Alerts</p>
                        <p className="text-2xl font-black text-slate-900">{latestAudit?.auditData?.dynamicPrompts ? (latestAudit.auditData.dynamicPrompts || []).filter(p => p && (!p.executionStatus || p.executionStatus !== 'completed')).length : 0}</p>
                    </div>
                </div>
            </div>

            {/* INSIGHTS AUDIT CENTER ALERTS (THE TO-DO LIST) */}
            {latestAudit && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-8">
                    <div className="bg-slate-900 px-6 py-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <BoltIcon className="w-6 h-6 text-amber-400" />
                            <h2 className="text-lg font-bold text-white">Insights Audit Center: Required Actions</h2>
                        </div>
                        <div className="flex items-center gap-4">
                            {!readOnly && <button
                                onClick={handleExecuteAll}
                                disabled={isExecutingAll || !(latestAudit?.auditData?.dynamicPrompts || []).some(p => p && (!p.executionStatus || p.executionStatus !== 'completed'))}
                                className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${
                                    isExecutingAll 
                                    ? 'bg-slate-800 text-slate-500 cursor-not-allowed' 
                                    : 'bg-amber-500 text-slate-900 hover:bg-amber-400 active:scale-95 shadow-lg shadow-amber-500/20'
                                }`}
                            >
                                {isExecutingAll ? (
                                    <>
                                        <ArrowPathIcon className="w-4 h-4 animate-spin" />
                                        Batch Executing...
                                    </>
                                ) : (
                                    <>
                                        <BoltIcon className="w-4 h-4" />
                                        Execute All Remediation
                                    </>
                                )}
                            </button>}
                            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                                latestAudit?.auditData?.healthStatus === 'Critical' ? 'bg-red-500/20 text-red-300 border border-red-500/30' :
                                latestAudit?.auditData?.healthStatus === 'Warning' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' :
                                'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                            }`}>
                                Posture: {latestAudit?.auditData?.healthStatus || 'Optimized'}
                            </span>
                        </div>
                    </div>
                    
                    <div className="p-6">
                        <p className="text-slate-700 font-medium mb-6 text-lg border-b border-slate-100 pb-4">
                            {latestAudit?.auditData?.executiveSummary || "No critical audit findings at this time. Organizational posture is within defined risk parameters."}
                        </p>
                        
                        <div className="space-y-4">
                            {/* 🚨 PERSONNEL CLEARANCE ALERT */}
                            {pendingUsersCount > 0 && (
                                <div 
                                    onClick={() => onNavigate({ page: 'myAccount' })}
                                    className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-5 rounded-xl bg-amber-50 border-2 border-amber-200 hover:border-amber-400 hover:shadow-lg transition-all cursor-pointer group animate-pulse-subtle"
                                >
                                    <div className="flex-shrink-0">
                                        <ShieldCheckIcon className="w-10 h-10 text-amber-600" />
                                    </div>
                                    <div className="flex-grow w-full">
                                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-1 gap-2">
                                            <p className="font-black text-amber-900 text-lg uppercase tracking-tight">Personnel Clearance Action Required</p>
                                            <span className="text-[10px] font-black text-amber-700 bg-amber-100 px-3 py-1 rounded-full border border-amber-200 whitespace-nowrap shadow-sm uppercase tracking-widest">
                                                Security Gate
                                            </span>
                                        </div>
                                        <p className="text-amber-800 text-sm font-medium">
                                            {pendingUsersCount} new {pendingUsersCount === 1 ? 'user' : 'users'} join {pendingUsersCount === 1 ? 'request' : 'requests'} identified via domain synchronization. Operational clearance is required to grant data access.
                                        </p>
                                    </div>
                                    <ArrowRightIcon className="w-6 h-6 text-amber-400 group-hover:text-amber-600 group-hover:translate-x-1 transition-all hidden sm:block" />
                                </div>
                            )}

                            {(latestAudit?.auditData?.dynamicPrompts || []).map((prompt, originalIndex) => {
                                if (!prompt || prompt.executionStatus === 'completed') return null;
                                const promptId = prompt.id || `prompt-${originalIndex}`;
                                const mapTargetSuite = (rawSuite: string): string => {
                                    const s = rawSuite.toLowerCase();
                                    if (s.includes('srm') || s.includes('security risk')) return 'Security Risk Management';
                                    if (s.includes('geopol') || s.includes('country')) return 'Comprehensive Country Geopolitical & Risk Analysis';
                                    if (s.includes('crisis') || s.includes('resilien')) return 'Company Business Resiliency and Crises Management Program';
                                    if (s.includes('exec') || s.includes('protect')) return 'Executive Protection';
                                    if (s.includes('threat') || s.includes('hub')) return 'Threat Analysis Hub';
                                    if (s.includes('travel')) return 'Travel Risk Management';
                                    if (s.includes('hsse') || s.includes('compliance')) return 'HSSE Compliance';
                                    return rawSuite;
                                };
                                const safeSuiteName = mapTargetSuite(prompt.targetSuite);
                                const isCyberAction = (prompt.triggerEvent + prompt.recommendedAction).toLowerCase().includes('cyber') || 
                                                     (prompt.triggerEvent + prompt.recommendedAction).toLowerCase().includes('ai-driven') ||
                                                     (prompt.triggerEvent + prompt.recommendedAction).toLowerCase().includes('data exfiltration') ||
                                                     (prompt.triggerEvent + prompt.recommendedAction).toLowerCase().includes('it infra');

                                return (
                                    <div 
                                        key={promptId} 
                                        className="relative flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all group overflow-hidden"
                                    >
                                        <div className="flex-shrink-0">
                                            {prompt.priority === 'High' ? (
                                                <ExclamationTriangleIcon className="w-8 h-8 text-red-500" />
                                            ) : prompt.priority === 'Medium' ? (
                                                <ShieldCheckIcon className="w-8 h-8 text-amber-500" />
                                            ) : (
                                                <CheckCircleIcon className="w-8 h-8 text-blue-500" />
                                            )}
                                        </div>
                                        <div className="flex-grow w-full">
                                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-1 gap-2">
                                                <p className="font-bold text-slate-900 text-base">{prompt.triggerEvent}</p>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[10px] font-black text-slate-400 bg-white px-3 py-1 rounded-full border border-slate-200 whitespace-nowrap uppercase tracking-widest shadow-sm">
                                                        {prompt.targetSuite}
                                                    </span>
                                                    {prompt.priority === 'High' && (
                                                        <span className="text-[10px] font-black text-red-600 bg-red-50 px-3 py-1 rounded-full border border-red-100 whitespace-nowrap uppercase tracking-widest shadow-sm">
                                                            Priority
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <p className="text-slate-600 text-sm mb-3">
                                                {prompt.recommendedAction}
                                            </p>

                                            {isCyberAction && (
                                                <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-100 flex items-start gap-3">
                                                    <ExclamationTriangleIcon className="w-5 h-5 text-red-600 mt-0.5" />
                                                    <div className="flex-grow">
                                                        <p className="text-[10px] font-black text-red-900 uppercase tracking-widest mb-1">Cybersecurity Pathway Advisory</p>
                                                        <p className="text-[10px] text-red-700 font-medium leading-relaxed">
                                                            This action involves specialized digital risk. **IntelOptics Insights is not a cybersecurity provider.** Users are MANDATED to seek assistance from a certified cybersecurity expert for deep technical remediation.
                                                        </p>
                                                    </div>
                                                </div>
                                            )}

                                            {!readOnly && <div className="flex flex-wrap items-center gap-3">
                                                {!remediationStates[promptId] || remediationStates[promptId]?.status === 'pending' || remediationStates[promptId]?.status === 'failed' ? (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleExecuteRemediation(promptId);
                                                        }}
                                                        className="flex items-center gap-2 px-4 py-1.5 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest rounded-lg hover:bg-blue-600 active:scale-95 transition-all shadow-sm group/btn"
                                                    >
                                                        <BoltIcon className="w-3.5 h-3.5 text-amber-400 group-hover/btn:animate-pulse" />
                                                        Auto-Architect Remedy
                                                    </button>
                                                ) : remediationStates[promptId]?.status === 'completed' ? (
                                                    <div className="flex flex-col gap-3 w-full">
                                                        <div className="flex items-center gap-3">
                                                            <span className={`flex items-center gap-1.5 px-3 py-1.5 bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px] font-black uppercase tracking-widest rounded-lg border`}>
                                                                <CheckCircleIcon className="w-3.5 h-3.5" />
                                                                Pathway Operationalized
                                                            </span>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onNavigate({ page: 'analysisDisplay', params: { reportId: remediationStates[promptId].resultId } });
                                                                }}
                                                                className="text-[10px] font-black text-blue-600 uppercase tracking-widest hover:underline"
                                                            >
                                                                View Strategic Pathway →
                                                            </button>
                                                        </div>
                                                        
                                                        {isCyberAction && (
                                                            <div className="p-2.5 bg-slate-900 rounded-lg border border-slate-700">
                                                                <p className="text-[9px] font-bold text-amber-400 uppercase tracking-tight mb-1">Legal Notice & Liability Disclaimer</p>
                                                                <p className="text-[9px] text-slate-300 leading-normal italic">
                                                                    IntelOptics Insights provides strategic guidance only. This report is a recommended set of actions intended for technical review and implementation by external cybersecurity professionals.
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : null}

                                                <button 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onNavigate({ 
                                                            page: 'suiteDashboard', 
                                                            params: { 
                                                                suiteName: safeSuiteName, 
                                                                workflowId: prompt.targetWorkflowId,
                                                                projectId: (prompt as any).targetProjectId,
                                                                auditId: latestAudit.id,
                                                                promptId: promptId
                                                            } 
                                                        });
                                                    }}
                                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-slate-500 text-[10px] font-black uppercase tracking-widest rounded-lg border border-slate-200 hover:border-slate-400 hover:text-slate-700 transition-all shadow-xs"
                                                >
                                                    <ArrowRightIcon className="w-3 h-3" />
                                                    Manual Intervention
                                                </button>

                                                <button 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleManualResolve(promptId);
                                                    }}
                                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-600 text-[10px] font-black uppercase tracking-widest rounded-lg border border-emerald-100 hover:bg-emerald-100 transition-all shadow-xs"
                                                >
                                                    <CheckCircleIcon className="w-3 h-3" />
                                                    Mark as Resolved
                                                </button>
                                            </div>}

                                            {remediationStates[promptId]?.status === 'executing' && (
                                                <div className="mt-4 p-4 rounded-xl bg-blue-50 border border-blue-100 animate-in fade-in slide-in-from-top-2 duration-300">
                                                    <div className="justify-between flex items-center mb-2">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-2 h-2 rounded-full bg-blue-500 animate-ping"></div>
                                                            <span className="text-[10px] font-bold text-blue-700 uppercase tracking-widest">
                                                                {remediationStates[promptId].statusText}
                                                            </span>
                                                        </div>
                                                        <span className="text-[10px] font-black text-blue-500">{remediationStates[promptId].progress}%</span>
                                                    </div>
                                                    <div className="w-full h-1.5 bg-blue-200 rounded-full overflow-hidden shadow-inner">
                                                        <div 
                                                            className="h-full bg-blue-600 transition-all duration-1000 ease-out shadow-lg shadow-blue-500/40 relative"
                                                            style={{ width: `${remediationStates[promptId].progress}%` }}
                                                        >
                                                            <div className="absolute top-0 right-0 h-full w-8 bg-gradient-to-l from-white/30 to-transparent"></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            {(latestAudit?.auditData?.dynamicPrompts || []).filter(p => p && (!p.executionStatus || p.executionStatus !== 'completed')).length === 0 && (
                                <div className="text-center py-12 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <CheckCircleIcon className="w-8 h-8 text-emerald-600" />
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-800">All Actions Resolved</h3>
                                    <p className="text-slate-500 text-sm">Organizational audit posture is currently optimized. Check back after the next scheduled sweep.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* STRATEGIC FORECASTING MODULE (STATE OF THE COMPANY) */}
            {latestAudit?.auditData?.strategicForecast && (
                <div ref={forecastSectionRef} tabIndex={-1} className="mb-8 scroll-mt-6 outline-none">
                    <div className="flex items-center gap-3 mb-4 px-2">
                        <PresentationChartLineIcon className="w-6 h-6 text-slate-800" />
                        <h2 className="text-xl font-black text-slate-900 tracking-tight">Enterprise Strategic Forecast</h2>
                        <span className="px-3 py-1 bg-slate-100 text-slate-500 text-xs font-bold uppercase tracking-wider rounded-md border border-slate-200 ml-2">
                            State of the Company
                        </span>
                        {forecastFeedback?.type === 'success' && (
                            <span className="px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-bold uppercase tracking-wider rounded-md border border-emerald-200">
                                Generated Now
                            </span>
                        )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Best Case (BCOA) */}
                        <div className="bg-white rounded-2xl shadow-sm border-t-4 border-t-emerald-500 border-x border-b border-slate-200 p-6 flex flex-col hover:shadow-md transition-shadow">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="p-2 bg-emerald-50 rounded-lg text-emerald-600">
                                    <CheckCircleIcon className="w-6 h-6" />
                                </div>
                                <h3 className="font-extrabold text-slate-800 text-lg uppercase tracking-wide">
                                    Best Case (BCOA)
                                </h3>
                            </div>
                            <p className="text-slate-600 font-medium text-sm leading-relaxed flex-grow">
                                {latestAudit.auditData.strategicForecast.bestCase?.description || "Awaiting intelligence processing."}
                            </p>
                        </div>

                        {/* Most Likely (MLCOA) */}
                        <div className="bg-white rounded-2xl shadow-sm border-t-4 border-t-indigo-500 border-x border-b border-slate-200 p-6 flex flex-col hover:shadow-md transition-shadow relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10"></div>
                            <div className="flex items-center gap-3 mb-3 relative z-10">
                                <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
                                    <PresentationChartLineIcon className="w-6 h-6" />
                                </div>
                                <h3 className="font-extrabold text-slate-800 text-lg uppercase tracking-wide">
                                    Most Likely (MLCOA)
                                </h3>
                            </div>
                            <p className="text-slate-600 font-medium text-sm leading-relaxed flex-grow relative z-10">
                                {latestAudit.auditData.strategicForecast.mlcoa?.description || "Awaiting intelligence processing."}
                            </p>
                        </div>

                        {/* Most Dangerous (MDCOA) */}
                        <div className="bg-white rounded-2xl shadow-sm border-t-4 border-t-rose-500 border-x border-b border-slate-200 p-6 flex flex-col hover:shadow-md transition-shadow">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="p-2 bg-rose-50 rounded-lg text-rose-600">
                                    <ExclamationTriangleIcon className="w-6 h-6" />
                                </div>
                                <h3 className="font-extrabold text-slate-800 text-lg uppercase tracking-wide">
                                    Most Dangerous (MDCOA)
                                </h3>
                            </div>
                            <p className="text-slate-600 font-medium text-sm leading-relaxed flex-grow">
                                {latestAudit.auditData.strategicForecast.mdcoa?.description || "Awaiting intelligence processing."}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* ORACLE VIEW: MAP & OPERATIONAL TRACKERS */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
                
                {/* MACRO VIEW: GLOBAL RISK ENGINES (span 2) */}
                <div className="lg:col-span-2 flex flex-col gap-8">
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden h-[800px] flex flex-col">
                        <RiskMonitorDashboard 
                            db={db} 
                            currentUser={currentUser}
                            currentOrganization={organization}
                            isLoaded={isLoaded} 
                            interactive={true} 
                            riskData={riskData || []} 
                            riskEvents={mappedAlerts} 
                            choroplethEnabled={true} 
                            onNavigate={onNavigate} 
                            externalFocus={focusedCrisis}
                            readOnly={readOnly}
                        />
                    </div>
                </div>

                {/* MICRO VIEW: OPERATIONAL TRACKERS (span 1) */}
                <div className="lg:col-span-1 flex flex-col gap-6">
                    
                    {/* SRM Command Center Link */}
                    <div className="bg-gradient-to-br from-indigo-900 to-slate-900 rounded-2xl shadow-lg border border-indigo-500/30 p-6 flex flex-col justify-between group overflow-hidden relative">
                        <div className="absolute -top-12 -right-12 w-48 h-48 bg-indigo-500/10 rounded-full blur-3xl group-hover:bg-indigo-500/20 transition-all"></div>
                        <div className="relative z-10">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2.5 bg-indigo-500/20 rounded-xl text-indigo-300 border border-indigo-500/40">
                                    <ShieldCheckIcon className="w-6 h-6" />
                                </div>
                                <h3 className="text-lg font-bold text-white tracking-tight">SRM Command Center</h3>
                            </div>
                            <p className="text-indigo-200/70 text-sm leading-relaxed mb-6">
                                Unified command for all risk architectures, deployed frameworks, and real-time operational telemetry.
                            </p>
                        </div>
                        {!readOnly && <button
                            onClick={() => onNavigate({ page: 'srm_command_center' })}
                            className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-black uppercase tracking-[0.2em] rounded-xl transition-all shadow-lg shadow-indigo-600/20 flex items-center justify-center gap-2 relative z-10"
                        >
                            Open SRM Command Center
                            <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>}
                    </div>

                    {/* Completed Workflows */}
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 flex-grow flex flex-col min-h-[288px]">
                        <div className="bg-slate-50 px-5 py-4 border-b border-slate-200 flex items-center justify-between rounded-t-2xl">
                            <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2 uppercase tracking-wide">
                                <CheckCircleIcon className="w-5 h-5 text-emerald-500" /> Completed/Deployed
                            </h3>
                            <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-1 rounded-md">{completedItems.length}</span>
                        </div>
                        <div className="p-4 flex-grow overflow-y-auto space-y-3">
                        {completedItems.map(item => (
                            <div 
                                key={item.id}
                                className={`p-4 bg-white border border-slate-100 rounded-xl transition-all group ${item.target ? 'cursor-pointer hover:shadow-md hover:border-emerald-300' : ''}`}
                                onClick={item.target ? () => onNavigate(item.target!) : undefined}
                            >
                                <div className="flex justify-between items-start mb-1">
                                    <p className="font-bold text-slate-800 text-sm group-hover:text-emerald-600 transition-colors line-clamp-1">{item.title}</p>
                                    {item.target && <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 text-slate-300 opacity-0 group-hover:opacity-100 transition-all" />}
                                </div>
                                <div className="flex items-center gap-2 text-xs text-slate-500">
                                    <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                                    {item.detail} · {formatLocalDate(item.completedAt)}
                                </div>
                            </div>
                        ))}
                        {completedItems.length === 0 && <p className="text-sm text-slate-500 text-center py-6">No completed deliveries.</p>}
                        </div>
                    </div>

                </div>
            </div>

            {/* SUITE ROUTING GRID (Bottom Navigation) */}
            {!readOnly && <div className="border-t border-slate-200 pt-8 mt-8">
                <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                    <BeakerIcon className="w-5 h-5 text-slate-500" /> Operational Suites
                    {pinnedSuites.length > 0 && (
                        <span className="ml-auto text-[10px] font-black uppercase tracking-widest text-indigo-500 flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></span>
                            {pinnedSuites.length} Pinned Suites
                        </span>
                    )}
                </h3>

                {/* Pinned Suites Quick-Access Bar */}
                {!readOnly && pinnedSuites.length > 0 && (
                    <div className="mb-6 p-4 bg-indigo-50 border border-indigo-100 rounded-2xl flex flex-wrap gap-2 items-center">
                        <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mr-1">Your Pinned Suites:</span>
                        {pinnedSuites.map((suiteName: string) => (
                            <button
                                key={suiteName}
                                onClick={() => onNavigate({ page: 'suiteDashboard', params: { suiteName } })}
                                className="px-3 py-1.5 bg-white border border-indigo-200 text-indigo-700 text-xs font-bold rounded-lg hover:bg-indigo-600 hover:text-white hover:border-indigo-600 transition-all shadow-sm flex items-center gap-1.5"
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-indigo-400"></span>
                                {suiteName}
                            </button>
                        ))}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Geopol */}
                    <div
                        onClick={readOnly ? undefined : () => onNavigate({ page: 'pillarsDashboard' })}
                        className={`group bg-white rounded-2xl border border-slate-200 shadow-sm transition-all duration-300 text-left overflow-hidden flex flex-col h-full relative ${readOnly ? '' : 'cursor-pointer hover:shadow-lg hover:border-blue-400'}`}
                    >
                        {pinnedSuites.some(s => s.toLowerCase().includes('geopolit') || s.toLowerCase().includes('country')) && (
                            <span className="absolute top-3 right-3 px-2 py-0.5 bg-indigo-600 text-white text-[9px] font-black uppercase tracking-widest rounded-full z-10">Pinned</span>
                        )}
                        <div className="p-6 flex-grow">
                            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                <GlobeAltIcon className="w-5 h-5 text-blue-600" />
                            </div>
                            <h3 className="text-lg font-bold text-slate-900 mb-2">Geopolitical Intelligence</h3>
                            <p className="text-slate-500 text-sm">
                                Access live OSINT threat feeds, daily country risk ratings, and organic actor tracking matrices.
                            </p>
                        </div>
                        {!readOnly && <div className="bg-slate-50 px-6 py-3 border-t border-slate-100 flex justify-between items-center mt-auto">
                            <span className="text-xs font-bold uppercase tracking-wider text-blue-600">Launch Suite</span>
                            <ArrowRightIcon className="w-4 h-4 text-blue-600 group-hover:translate-x-1 transition-transform" />
                        </div>}
                    </div>

                    {/* Enterprise Resiliency Hub */}
                    <div
                        onClick={readOnly ? undefined : () => onNavigate({ page: 'resiliencyHub' })}
                        className={`group bg-white rounded-2xl border border-slate-200 shadow-sm transition-all duration-300 text-left overflow-hidden flex flex-col h-full md:col-span-2 relative ${readOnly ? '' : 'cursor-pointer hover:shadow-lg hover:border-indigo-500'}`}
                    >
                        {pinnedSuites.some(s => s.toLowerCase().includes('srm') || s.toLowerCase().includes('resili') || s.toLowerCase().includes('crisis') || s.toLowerCase().includes('hsse')) && (
                            <span className="absolute top-3 right-3 px-2 py-0.5 bg-indigo-600 text-white text-[9px] font-black uppercase tracking-widest rounded-full z-10">Pinned</span>
                        )}
                        <div className="p-6 flex-grow relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-5"></div>
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-sm">
                                    <ShieldCheckIcon className="w-6 h-6 text-indigo-600" />
                                </div>
                                <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-sm delay-75">
                                    <BeakerIcon className="w-6 h-6 text-emerald-600" />
                                </div>
                            </div>
                            <h3 className="text-xl font-black text-slate-900 mb-2">Enterprise Resiliency Hub</h3>
                            <p className="text-slate-500 text-sm leading-relaxed max-w-xl">
                                Consolidated workspace for proactive Risk Architecture (SRM) and Operational Crisis Activation. 
                                Manage localized vulnerability blueprints and deploy rapid-response frameworks from a unified command interface.
                            </p>
                            
                            <div className="mt-4 flex flex-wrap gap-2">
                                <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-[10px] font-bold uppercase tracking-wider rounded-md border border-slate-200">
                                    Vulnerability Logic
                                </span>
                                <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-[10px] font-bold uppercase tracking-wider rounded-md border border-slate-200">
                                    Incident Logistics
                                </span>
                                <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-[10px] font-bold uppercase tracking-wider rounded-md border border-slate-200">
                                    Resiliency Blueprinting
                                </span>
                            </div>
                        </div>
                        {!readOnly && <div className="bg-slate-900 px-6 py-4 flex justify-between items-center mt-auto">
                            <div className="flex flex-col">
                                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-400">Tactical Command</span>
                                <span className="text-white font-bold text-sm">Enter Resiliency Workspace</span>
                            </div>
                            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center text-white group-hover:translate-x-1 transition-transform">
                                <ArrowRightIcon className="w-5 h-5" />
                            </div>
                        </div>}
                    </div>
                </div>
            </div>}

            {isGeneratingExecReport && (
                <div className="fixed inset-0 z-[110] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center">
                    <div className="text-center bg-[#0B1121] p-8 rounded-2xl border border-slate-700 shadow-2xl">
                        <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-6 shadow-lg shadow-indigo-500/50"></div>
                        <h3 className="text-xl font-bold text-white mb-2 tracking-widest uppercase">Synthesizing Executive Report</h3>
                        <p className="text-slate-400 font-mono text-xs">Cross-referencing global risk anomalies against organizational assets...</p>
                    </div>
                </div>
            )}

            {!readOnly && <ExecutiveReportBuilder
                isOpen={isReportBuilderOpen} 
                onClose={() => setIsReportBuilderOpen(false)} 
                onGenerate={async (config: ReportConfig) => {
                    if (!organization.industry || !organization.region) {
                        void showAppAlert("Missing Organizational Metadata: Please configure Industry and Region in Company Settings before generating reports to ensure grounded analysis.");
                        setIsReportBuilderOpen(false);
                        setIsSettingsOpen(true);
                        return;
                    }
                    setIsReportBuilderOpen(false);
                    setIsGeneratingExecReport(true);
                    try {
                        const generateExec = httpsCallable(functions, 'generateExecutiveReport');
                        const res = await generateExec({ config, organizationId: organization?.id });
                        const response = res.data as any;
                        if (!response?.reportId)
                            throw new Error('Executive report job did not return a report identifier.');
                        onNavigate({ page: 'executiveReport', params: { config, reportId: response.reportId } });
                    } catch (err: any) {
                        console.error('Failed to generate executive report:', err);
                        void showAppAlert("Failed to generate report. Please try again.");
                    } finally {
                        setIsGeneratingExecReport(false);
                    }
                }} 
            />}

            {!readOnly && <EnterpriseFsatEngine
                isOpen={isFsatEngineOpen}
                onClose={() => setIsFsatEngineOpen(false)}
                organization={organization!}
                assets={assets}
                onComplete={(result) => {
                    setIsFsatEngineOpen(false);
                    onNavigate({ page: 'analysisDisplay', params: { result } });
                }}
            />}

            {!readOnly && <EnterpriseAuditEngine
                isOpen={isAuditEngineOpen}
                onClose={() => setIsAuditEngineOpen(false)}
                organization={organization!}
                assets={assets}
                activeThreatsCount={activeHighCriticalThreats}
                onComplete={(result) => {
                    setIsAuditEngineOpen(false);
                    onNavigate({ page: 'analysisDisplay', params: { result } });
                }}
            />}

            <BusinessMethodologySidebar 
                isOpen={isMethodologyOpen} 
                onClose={() => setIsMethodologyOpen(false)} 
            />

            {/* RESOLUTION CONFIRMATION OVERLAY */}
            {!readOnly && showResolutionConfirm && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md cursor-pointer" onClick={() => setShowResolutionConfirm(false)}></div>
                    <div className="relative bg-white rounded-3xl shadow-2xl border border-slate-200 max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="bg-emerald-500 h-2 w-full"></div>
                        <div className="p-8 text-center text-slate-800">
                            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm border-4 border-white">
                                <CheckCircleIcon className="w-10 h-10 text-emerald-600" />
                            </div>
                            <h3 className="text-2xl font-black text-slate-900 mb-4 tracking-tight leading-none uppercase italic">Remediation Synchronized</h3>
                            <p className="text-slate-600 font-medium leading-relaxed mb-8">
                                {resolutionMsg}
                            </p>
                            <div className="flex flex-col gap-3">
                                {resultProjectId && (
                                    <button 
                                        onClick={() => {
                                            setShowResolutionConfirm(false);
                                            onNavigate({ 
                                                page: 'suiteDashboard', 
                                                params: { 
                                                    suiteName: 'Security Risk Management', 
                                                    workflowId: 'project_dashboard',
                                                    projectId: resultProjectId 
                                                } 
                                            });
                                            setResultProjectId(null);
                                        }}
                                        className="w-full py-4 bg-blue-600 text-white font-black uppercase tracking-[0.2em] rounded-2xl hover:bg-blue-700 hover:shadow-xl hover:-translate-y-0.5 transition-all active:scale-95 text-xs shadow-lg shadow-blue-900/10 flex items-center justify-center gap-2"
                                    >
                                        <ArrowRightIcon className="w-4 h-4 text-white" />
                                        View SRM Project Risks
                                    </button>
                                )}
                                <button 
                                    onClick={() => {
                                        setShowResolutionConfirm(false);
                                        onNavigate({ page: 'global_risk_register' });
                                        setResultProjectId(null);
                                    }}
                                    className="w-full py-4 bg-indigo-600 text-white font-black uppercase tracking-[0.2em] rounded-2xl hover:bg-indigo-700 hover:shadow-xl hover:-translate-y-0.5 transition-all active:scale-95 text-xs shadow-lg shadow-indigo-900/10 flex items-center justify-center gap-2"
                                >
                                    <ShieldExclamationIcon className="w-4 h-4 text-white" />
                                    Open Master Register
                                </button>
                                <button 
                                    onClick={() => {
                                        setShowResolutionConfirm(false);
                                        setResultProjectId(null);
                                    }}
                                    className="w-full py-4 bg-slate-900 text-white font-black uppercase tracking-[0.2em] rounded-2xl hover:bg-slate-800 hover:shadow-xl hover:-translate-y-0.5 transition-all active:scale-95 text-xs shadow-lg shadow-slate-900/10"
                                >
                                    Acknowledge & Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );

};

export default OrganizationDashboard;
